import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Trash2, FileText, Percent, Calendar, CheckCircle, AlertCircle, IndianRupee } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

export default function InstallmentPlans({ user, onLogout }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const [formData, setFormData] = useState({
    plan_name: '',
    description: '',
    plan_type: 'percentage',
    installments: [{ installment_name: 'Booking Amount', percentage: 0, amount: 0, months_from_booking: 0 }]
  });

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/installment-plans');
      setPlans(response.data);
    } catch { toast.error('Failed to load plans'); }
  };

  const resetForm = () => {
    setFormData({
      plan_name: '', description: '', plan_type: 'percentage',
      installments: [{ installment_name: 'Booking Amount', percentage: 0, amount: 0, months_from_booking: 0 }]
    });
    setEditingPlan(null);
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10, k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const handleAddInstallment = () => {
    const count = formData.installments.length;
    setFormData({
      ...formData,
      installments: [
        ...formData.installments,
        { installment_name: count === 0 ? 'Booking Amount' : `${count + 1}${getOrdinalSuffix(count + 1)} Installment`, percentage: 0, amount: 0, months_from_booking: count * 2 }
      ]
    });
  };

  const handleRemoveInstallment = (index) => {
    if (formData.installments.length <= 1) { toast.error('Kam se kam ek installment honi chahiye'); return; }
    setFormData({ ...formData, installments: formData.installments.filter((_, i) => i !== index) });
  };

  const handleInstallmentChange = (index, field, value) => {
    const updated = [...formData.installments];
    updated[index] = { ...updated[index], [field]: field === 'installment_name' ? value : parseFloat(value) || 0 };
    setFormData({ ...formData, installments: updated });
  };

  const getTotalPercentage = () => formData.installments.reduce((sum, inst) => sum + (inst.percentage || 0), 0);
  const getTotalAmount = () => formData.installments.reduce((sum, inst) => sum + (inst.amount || 0), 0);

  const isValid = () => {
    if (formData.plan_type === 'percentage') return Math.abs(getTotalPercentage() - 100) < 0.01;
    return getTotalAmount() > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.plan_name) { toast.error('Plan name daale'); return; }
    if (!isValid()) {
      if (formData.plan_type === 'percentage') toast.error(`Total percentage 100% honi chahiye. Abhi: ${getTotalPercentage()}%`);
      else toast.error('Total amount 0 se zyada hona chahiye');
      return;
    }
    setLoading(true);
    try {
      if (editingPlan) {
        await api.put(`/installment-plans/${editingPlan.id}`, formData);
        toast.success('Plan updated!');
      } else {
        await api.post('/installment-plans', formData);
        toast.success('Plan created!');
      }
      setDialogOpen(false); resetForm(); fetchPlans();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save plan'); }
    finally { setLoading(false); }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      plan_name: plan.plan_name,
      description: plan.description || '',
      plan_type: plan.plan_type || 'percentage',
      installments: plan.installments.map(i => ({ ...i, amount: i.amount || 0, percentage: i.percentage || 0 }))
    });
    setDialogOpen(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Kya aap sure hain?')) return;
    try { await api.delete(`/installment-plans/${planId}`); toast.success('Plan deleted!'); fetchPlans(); }
    catch { toast.error('Failed to delete plan'); }
  };

  const totalPercentage = getTotalPercentage();
  const totalAmount = getTotalAmount();

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="installment-plans-page">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <FileText className="text-emerald-600" />
                  Installment Plans
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">Percentage ya Amount based installment plans banaye</p>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-plan-button" className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                    <Plus size={18} /> New Plan
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Plan Name *</Label>
                        <Input data-testid="plan-name-input" placeholder="e.g., 3 Installments Plan"
                          value={formData.plan_name} onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input data-testid="plan-description-input" placeholder="Brief description"
                          value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                      </div>
                    </div>

                    {/* Plan Type Toggle */}
                    <div className="space-y-2">
                      <Label>Plan Type</Label>
                      <div className="flex bg-slate-100 rounded-xl p-1 gap-1" data-testid="plan-type-toggle">
                        <button type="button" onClick={() => setFormData({ ...formData, plan_type: 'percentage' })}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            formData.plan_type === 'percentage' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                          }`}>
                          <Percent size={16} /> Percentage (%)
                        </button>
                        <button type="button" onClick={() => setFormData({ ...formData, plan_type: 'amount' })}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            formData.plan_type === 'amount' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                          }`}>
                          <IndianRupee size={16} /> Amount (Rs)
                        </button>
                      </div>
                    </div>

                    {/* Installments */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">Installments</Label>
                        {formData.plan_type === 'percentage' ? (
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            isValid() ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            Total: {totalPercentage}%
                            {isValid() ? <CheckCircle size={14} className="inline ml-1" /> : <AlertCircle size={14} className="inline ml-1" />}
                          </div>
                        ) : (
                          <div className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                            Total: {formatCurrency(totalAmount)}
                          </div>
                        )}
                      </div>

                      {formData.installments.map((inst, index) => (
                        <div key={index} className="flex items-end gap-2 p-3 bg-slate-50 rounded-xl">
                          <div className="flex-1 min-w-0">
                            <Label className="text-xs text-slate-500">Name</Label>
                            <Input placeholder="Installment Name" value={inst.installment_name}
                              onChange={(e) => handleInstallmentChange(index, 'installment_name', e.target.value)} className="h-9" />
                          </div>

                          {formData.plan_type === 'percentage' ? (
                            <div className="w-24 shrink-0">
                              <Label className="text-xs text-slate-500">Percentage</Label>
                              <div className="relative">
                                <Input type="number" min="0" max="100" placeholder="0" value={inst.percentage || ''}
                                  onChange={(e) => handleInstallmentChange(index, 'percentage', e.target.value)} className="h-9 pr-8" />
                                <Percent size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-32 shrink-0">
                              <Label className="text-xs text-slate-500">Amount (Rs)</Label>
                              <div className="relative">
                                <Input type="number" min="0" placeholder="0" value={inst.amount || ''}
                                  onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)} className="h-9 pl-7" />
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-medium">Rs</span>
                              </div>
                            </div>
                          )}

                          <div className="w-24 shrink-0">
                            <Label className="text-xs text-slate-500">Months</Label>
                            <div className="relative">
                              <Input type="number" min="0" placeholder="0" value={inst.months_from_booking || ''}
                                onChange={(e) => handleInstallmentChange(index, 'months_from_booking', e.target.value)} className="h-9 pr-8" />
                              <Calendar size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            </div>
                          </div>
                          <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveInstallment(index)} className="h-9 px-2.5">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ))}

                      <Button type="button" variant="outline" onClick={handleAddInstallment} className="w-full gap-2">
                        <Plus size={16} /> Add Installment
                      </Button>
                    </div>

                    {!isValid() && formData.plan_type === 'percentage' && (
                      <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                        Total percentage 100% honi chahiye. Abhi: {totalPercentage}%
                      </p>
                    )}

                    <Button type="submit" disabled={loading || !isValid()} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12">
                      {loading ? 'Saving...' : editingPlan ? 'Update Plan' : 'Create Plan'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
        </Card>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <Card key={plan.id} className="hover:shadow-lg transition-shadow" data-testid={`plan-card-${plan.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{plan.plan_name}</CardTitle>
                    {plan.description && <p className="text-sm text-slate-500 mt-1">{plan.description}</p>}
                  </div>
                  <div className="flex gap-1.5">
                    <Badge variant="outline" className={plan.plan_type === 'amount' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}>
                      {plan.plan_type === 'amount' ? 'Amount' : 'Percentage'}
                    </Badge>
                    <Badge variant="outline" className="bg-slate-50">
                      {plan.installments.length} installments
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {plan.installments.map((inst, index) => (
                    <div key={index} className="flex items-center justify-between text-sm p-2.5 bg-slate-50 rounded-lg">
                      <span className="text-slate-600 truncate flex-1">{inst.installment_name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {(plan.plan_type || 'percentage') === 'amount' ? (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700">{formatCurrency(inst.amount)}</Badge>
                        ) : (
                          <Badge variant="secondary">{inst.percentage}%</Badge>
                        )}
                        <span className="text-xs text-slate-400">
                          {inst.months_from_booking === 0 ? 'Booking pe' : `+${inst.months_from_booking} months`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {(plan.plan_type || 'percentage') === 'amount' && (
                  <div className="text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-2 rounded-lg text-center">
                    Total: {formatCurrency(plan.installments.reduce((s, i) => s + (i.amount || 0), 0))}
                  </div>
                )}
                <div className="flex gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(plan)} className="flex-1 gap-1">
                    <Edit2 size={14} /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(plan.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {plans.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-lg font-medium text-slate-600">Abhi koi plan nahi hai</p>
                <p className="text-slate-500">Apna pehla installment plan banaye</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
